/**
 * Negotiation-grade claim ledger (T33-0011 Pass 1 output).
 *
 * Claims are authority-bearing statements extracted from a single chat with a
 * required verbatim quote. They are NOT ideation graph nodes — promote later.
 */
import { z } from "zod";
export const ClaimSpeakerSchema = z.enum(["human", "assistant"]);
export const ClaimKindSchema = z.enum([
    "DECISION",
    "FACT",
    "CORRECTION",
    "SUPERSESSION",
    "RULE",
    "OPEN",
    "ARTEFACT",
    "DISCLOSURE",
    "PROPOSAL",
]);
export const ClaimConfidenceSchema = z.enum(["EXPLICIT", "HEDGED"]);
export const ClaimFlagSchema = z.enum([
    "CONTRADICTS_IN_CHAT",
    "AMBIGUOUS_AGREEMENT",
    "ORDERING_UNRELIABLE",
]);
/**
 * One claim line from Pass 1 (JSONL). Schema enforces speaker/kind authority:
 * assistant may not emit DECISION or FACT.
 */
export const ClaimSchema = z
    .object({
    schemaVersion: z.literal(1).optional().default(1),
    chatId: z.string().min(1),
    chatUpdatedAt: z.string().datetime({ offset: true }).or(z.string().min(1)),
    turnIndex: z.number().int().nonnegative(),
    /** Human turn that authorised an Assistant proposal (agreement). */
    authorityTurnIndex: z.number().int().nonnegative().nullable().default(null),
    /** Assistant turn that supplied proposal content when Human agreed. */
    contentTurnIndex: z.number().int().nonnegative().nullable().optional(),
    speaker: ClaimSpeakerSchema,
    kind: ClaimKindSchema,
    subject: z.string().min(1).max(200),
    value: z.string().min(1).max(2000),
    quote: z.string().min(1).max(400),
    confidence: ClaimConfidenceSchema,
    flags: z.array(ClaimFlagSchema).default([]),
    /** Model id that produced this claim (per-call override recorded). */
    model: z.string().min(1).optional(),
})
    .superRefine((claim, ctx) => {
    const words = claim.quote.trim().split(/\s+/).filter(Boolean);
    if (words.length > 25) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "quote must be at most 25 words",
            path: ["quote"],
        });
    }
    if (claim.speaker === "assistant" && (claim.kind === "DECISION" || claim.kind === "FACT")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "assistant speaker cannot emit DECISION or FACT",
            path: ["kind"],
        });
    }
});
/** Parse one JSONL line; returns null if empty. Throws ZodError on invalid. */
export function parseClaimLine(line) {
    const trimmed = line.trim();
    if (!trimmed)
        return null;
    return ClaimSchema.parse(JSON.parse(trimmed));
}
//# sourceMappingURL=claims.js.map